/* Next */
import Link from 'next/link'
import { useRouter } from 'next/router'

/* Redux */
import DrawerRedux from './Drawer.Redux'
import NavigationRedux from './Navigation.Redux'
import { hrefForNavigationItem } from 'src/functions/navigationBreadcrumbs'

/* Material */
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import ListItemIcon from '@mui/material/ListItemIcon'
import IconButton from '@mui/material/IconButton'
import useStyles from './styles'

/* Material-Icons */
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowRightIcon from '@mui/icons-material/ArrowRight'

/* Types */
import { tMenu, item } from 'src/shared/frontendTypes/navigation'

interface Props {
  paths: tMenu
  activeMenuId: number | null
  activeBrandId: number | null
}

const NavigationMenuPages: React.FC<Props> = ({ paths, activeMenuId, activeBrandId }) => {
  const classes = useStyles()
  const router = useRouter()
  const url = router.asPath

  const { toggle: toggleDrawer } = DrawerRedux()

  const { 
    displayMenuId, setDisplayMenuId,
    displayFilter, setDisplayFilter,
    resetFilter,
  } = NavigationRedux()

  const menuPages: JSX.Element[] = []
  const visibleMenuId = getRenderableMenuId(paths, displayMenuId)

  /* ############################################################################ */
  const renderMenu = (
    paths: tMenu,
    mid: number,
    parent: number,
    parentBrand: item | null,
  ) => {
    const links: JSX.Element[] = []

    const renderLink = (entry: item) => {
      const { id, menueId, title, brand, children } = entry
      const href = hrefForNavigationItem(entry, parentBrand) || '#'

      const hasChildren = Boolean(children?.length)
      const hasTarget = href !== '#'
      const isSelected = activeMenuId === menueId || activeBrandId === brand

      const handleTargetClick = async (e: React.MouseEvent<HTMLElement>) => {
        if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return

        if (router.asPath === href || router.asPath === href + '#') {
          e.preventDefault()
          await router.replace(href)
          return
        }

        if (hasTarget) {
          if (!brand && menueId) setDisplayFilter(true)
          resetFilter()
          toggleDrawer(e)
        }
      }

      if (hasChildren) {
        return <ListItem
          key={id}
          component='li'
          divider
          selected={isSelected}
          className={classes.menuBranchListItem}
        >
          <button
            type='button'
            className={classes.menuBranchButton}
            onClick={event => {
              event.preventDefault()
              setDisplayMenuId(id)
            }}
          >
            <ListItemIcon><ArrowDropDownIcon /></ListItemIcon>
            <ListItemText primary={title} />
          </button>

          {hasTarget &&
            <Link legacyBehavior passHref href={href}>
              <IconButton
                component='a'
                className={classes.menuTargetButton}
                aria-label={`${title} öffnen`}
                title={`${title} öffnen`}
                onClick={handleTargetClick}
                size='large'
              >
                <span className={classes.visuallyHidden}>{title}</span>
                <ArrowRightIcon />
              </IconButton>
            </Link>
          }
        </ListItem>
      }

      const listItem = (
        <ListItem
          key={id}
          component='li' button divider
          selected={isSelected}
          onClick={handleTargetClick}
        >
          <ListItemIcon><ArrowRightIcon /></ListItemIcon>
          <ListItemText primary={title} />
        </ListItem>
      )

      return (
        <Link key={id} legacyBehavior href={href}>
          <a>{listItem}</a>
        </Link>
      )
    }

    for (const pathObj of paths) {
      links.push(renderLink(pathObj));
      if (pathObj.children) {
        renderMenu(
          pathObj.children,
          pathObj.id, // Verwende die ID des aktuellen Menüpunktes als neuen mid.
          mid, // Der übergeordnete Wert bleibt gleich.
          pathObj.brand ? pathObj : parentBrand,
        );
      }
    }

    const backButton = (
      <ListItem component='li' disablePadding divider>
        <ListItemButton component='button' type='button' sx={{ width: '100%' }}
          onClick={(e: React.MouseEvent)=>{
            e.preventDefault();
            setDisplayMenuId(parent);
          }}
        >
          <ListItemIcon><ArrowBackIcon /></ListItemIcon>
          <ListItemText primary='Zurück' />
        </ListItemButton>
      </ListItem>
    );

    if (!displayFilter && visibleMenuId === mid)
      menuPages.push(
        <List key={`menu-${mid}`} className={classes.list}>
          {mid !== 0 && backButton}
          {links}
        </List>
      );
  };

  renderMenu(paths, 0, 0, null);

  return <>{menuPages}</>;
}

export default NavigationMenuPages;

const getRenderableMenuId = (paths: tMenu, displayMenuId: number): number => {
  if (displayMenuId === 0) return 0

  const findRenderableId = (items: tMenu, parent: number): number | null => {
    for (const item of items) {
      if (item.id === displayMenuId) {
        return item.children?.length ? item.id : parent
      }

      if (item.children?.length) {
        const childMatch = findRenderableId(item.children, item.id)
        if (childMatch !== null) return childMatch
      }
    }

    return null
  }

  return findRenderableId(paths, 0) ?? 0
}
